import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@eon/ui';
import { hooks, type OmUser } from '@eon/api-client';
import { AllowanceBalance } from '@/components/AllowanceBalance';

interface AllowanceDialogProps {
  user: OmUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  amount: string;
  decrease: boolean;
}

export function AllowanceDialog({ user, open, onOpenChange }: AllowanceDialogProps) {
  const { t } = useTranslation();
  const adjust = hooks.useOmAdjustAllowance();
  const { register, handleSubmit, reset, watch, setValue, formState } = useForm<FormValues>({
    defaultValues: { amount: '', decrease: false },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!user) return;
    const amount = Number.parseFloat(values.amount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    try {
      await adjust.mutateAsync({
        userId: user.userId,
        amount,
        decrease: values.decrease,
      });
      toast.success(t('portal.users.allowanceUpdated'));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('portal.users.allowanceTitle')}</DialogTitle>
        </DialogHeader>
        {user ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <p className="text-muted-foreground text-sm">{user.name ?? user.email}</p>
            <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
              <span className="text-muted-foreground text-sm">{t('portal.users.allowanceCurrent')}</span>
              <AllowanceBalance value={user.remainingAllowance ?? user.allowanceKwh} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="allowance-amount">{t('portal.users.allowanceAmount')}</Label>
              <Input
                id="allowance-amount"
                type="number"
                min={0}
                step="0.1"
                {...register('amount', { required: true })}
              />
            </div>
            <CheckboxField
              checked={watch('decrease')}
              onCheckedChange={(c) => setValue('decrease', c)}
              label={t('portal.users.allowanceDecrease')}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={formState.isSubmitting || adjust.isPending}>
                {t('actions.save')}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
